import { useEffect, useMemo, useState } from 'react';

export interface CaseSubmission {
  id: string;
  author: string;
  text: string;
  link: string;
  at: string;
  accepted: boolean;
}

export interface CaseItem {
  id: string;
  title: string;
  difficulty: 1 | 2 | 3;
  points: number;
  status: 'open' | 'solved';
  brief: string;
  hints: string[];
  submissions: CaseSubmission[];
  solver?: string;
  solvedAt?: string;
  image?: string;
  credit?: string;
}

interface CaseState {
  cases: CaseItem[];
  leaderboard: Record<string, number>;
}

const KEY = 'zenith_cases_v1';

const SEED: CaseItem[] = [
  {
    id: 'case-1',
    title: 'Geo-Locate: Diese Brücke',
    difficulty: 1,
    points: 100,
    status: 'open',
    image: '/cases/case1.jpg',
    credit: 'Photo: Graham Horn (geograph.org.uk) · CC BY-SA 2.0',
    brief:
      'Das Foto zeigt eine Stahlbrücke über einen Fluss, direkt daneben ein markanter Wasserturm. Hinweis: Aufnahme aus einem fahrenden Zug, Blickrichtung Osten. Ermittle den Standort (Land + nächster Ort + Brückenname). Tipp: Suche nach dem Wasserturm als Anker — er ist auf Luftbildern unverwechselbar.',
    hints: [
      'Der Ort liegt in England, in flachem Marschland.',
      'Der Wasserturm steht auf der Südseite der Bahnstrecke.',
      'Der Fluss ist ein schmaler Entwässerungskanal, kein großer Strom.',
    ],
    submissions: [],
  },
  {
    id: 'case-2',
    title: 'Identifiziere die Einheit',
    difficulty: 2,
    points: 150,
    status: 'open',
    image: '/cases/case2.jpg',
    credit: 'U.S. Army photo · Public Domain',
    brief:
      'Ein 20-Sekunden-Video zeigt einen Militärkonvoi auf einer Landstraße: 6 Fahrzeuge, das zweite ist ein Bergepanzer auf Kettenfahrwerk. Tarnmuster: dreifarbig, weiche Flecken (Woodland). Kein taktisches Zeichen sichtbar. Welcher Typ? Welches Land setzt dieses Muster ein?',
    hints: [
      'Der Bergepanzer basiert auf einem Kampfpanzer-Chassis aus den 1960ern.',
      'Die Winde ist vorne am Wannenbug montiert, der Kran ist fest mit der Wanne verbunden.',
      'Das Tarnmuster wurde ab den 1980ern standardmäßig von genau dieser Armee gefahren.',
    ],
    submissions: [],
  },
  {
    id: 'case-3',
    title: 'Chrono-Locate: Wann wurde das Foto gemacht?',
    difficulty: 3,
    points: 200,
    status: 'open',
    image: '/cases/case3.jpg',
    credit: 'U.S. Air Force photo · Public Domain',
    brief:
      'Standbild aus einem Video: eine Ski-Transportmaschine (C-130) auf einer Schneepiste in Grönland. EXIF ist entfernt. Messwerte aus der Schattenanalyse: Schattenrichtung ≈ 70° (Nordost), Schattenverhältnis Länge/Höhe = 1,5. Aufnahmebreite ≈ 67°N, Datum 21. Juni. Bestimme die ungefähre Ortszeit (±30 min). Nutze das Sun/Shadow-Tool in der Workbench.',
    hints: [
      'Schattenrichtung Nordost ⇒ Sonne steht im Südwesten ⇒ Nachmittag.',
      'Höhenwinkel: tan(α) = 1/1,5 ⇒ α ≈ 34°.',
      'Bei 67°N im Juni: Mitternachtssonne — prüfe, ob der Vormittag eine zweite Lösung ergäbe (er ergibt keine: dann stünde die Sonne im Osten).',
    ],
    submissions: [],
  },
  {
    id: 'case-4',
    title: 'Open Challenge: Eigener Fall',
    difficulty: 3,
    points: 250,
    status: 'open',
    brief:
      'Reiche selbst einen Fall ein: ein Foto, Video oder eine Behauptung, die du geolokalisiert, chronolokalisiert oder identifiziert haben willst. Die Community löst deine Challenge — du vergibst die Punkte.',
    hints: ['Nur Material, das du selbst aufgenommen oder frei lizenziert hast.', 'Je klarer die Hinweise, desto mehr Beteiligung.'],
    submissions: [],
  },
];

function load(): CaseState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CaseState;
      if (Array.isArray(parsed.cases) && parsed.cases.length) return parsed;
    }
  } catch {
    /* fallthrough */
  }
  return { cases: SEED, leaderboard: {} };
}

export function useCases() {
  const [state, setState] = useState<CaseState>(() => load());

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* storage full or blocked */
    }
  }, [state]);

  const submit = (caseId: string, author: string, text: string, link: string) => {
    const sub: CaseSubmission = {
      id: `s-${Date.now()}`,
      author: author.trim() || 'ANON',
      text: text.trim(),
      link: link.trim(),
      at: new Date().toISOString(),
      accepted: false,
    };
    setState((s) => ({
      ...s,
      cases: s.cases.map((c) => (c.id === caseId ? { ...c, submissions: [...c.submissions, sub] } : c)),
    }));
    return sub;
  };

  const accept = (caseId: string, submissionId: string) => {
    setState((s) => {
      const item = s.cases.find((c) => c.id === caseId);
      const sub = item?.submissions.find((x) => x.id === submissionId);
      if (!item || !sub || item.status === 'solved') return s;
      const next = {
        ...s,
        cases: s.cases.map((c) =>
          c.id === caseId
            ? {
                ...c,
                status: 'solved' as const,
                solver: sub.author,
                solvedAt: new Date().toISOString(),
                submissions: c.submissions.map((x) => (x.id === submissionId ? { ...x, accepted: true } : x)),
              }
            : c,
        ),
        leaderboard: { ...s.leaderboard, [sub.author]: (s.leaderboard[sub.author] ?? 0) + item.points },
      };
      return next;
    });
  };

  const leaderboard = useMemo(
    () => Object.entries(state.leaderboard).sort((a, b) => b[1] - a[1]),
    [state.leaderboard],
  );

  return { cases: state.cases, leaderboard, submit, accept };
}
